import type { CharacterSummary } from '@shefira/shared';

import { AuthError } from '../net/session.js';
import { closeButton } from './hud.js';
import { uiFormat, uiText } from './text.js';
import './shop-modals.css';

/**
 * The last step before a character is gone: its name typed again, so a
 * stray click deletes nothing. « Supprimer » stays grey until the name
 * matches; the server checks it again. A refusal (in play, shop purchases)
 * is shown in the window, which stays open.
 *
 * Resolves true once the server has deleted the character, false if the
 * player backed out.
 */
export function confirmCharacterDeletion(
  character: CharacterSummary,
  remove: (name: string) => Promise<void>,
): Promise<boolean> {
  return confirmDeletion({
    label: uiFormat('Supprimer {name}', { name: character.name }),
    title: uiFormat('Supprimer {name} ?', { name: character.name }),
    lead: uiText(
      'Son équipement, son or, ses quêtes et ses succès disparaissent pour toujours. Tape son nom pour confirmer.',
    ),
    expected: character.name,
    fieldLabel: uiText('Nom du personnage'),
    remove,
  });
}

/**
 * The whole account (ADR 0075): every character, the purchases they hold,
 * the linked services. The word to type is the button's own, in the
 * player's language.
 */
export function confirmAccountDeletion(remove: () => Promise<void>): Promise<boolean> {
  const word = uiText('SUPPRIMER');
  return confirmDeletion({
    label: uiText('Supprimer mon compte'),
    title: uiText('Supprimer ton compte ?'),
    lead: uiFormat(
      'Tous tes personnages, leur équipement, leur or et tes achats disparaissent pour toujours, sans remboursement. Tape {word} pour confirmer.',
      { word },
    ),
    expected: word,
    fieldLabel: uiText('Mot de confirmation'),
    remove: () => remove(),
  });
}

interface DeletionDialog {
  /** The dialog's accessible name. */
  label: string;
  title: string;
  lead: string;
  /** What must be typed, compared without case. */
  expected: string;
  fieldLabel: string;
  remove: (typed: string) => Promise<void>;
}

function confirmDeletion({
  label,
  title: heading,
  lead: hint,
  expected,
  fieldLabel,
  remove,
}: DeletionDialog): Promise<boolean> {
  return new Promise((resolve) => {
    const root = document.createElement('div');
    root.className = 'shop-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', label);

    const frame = document.createElement('section');
    frame.className = 'shop-modal__window';
    const title = document.createElement('h2');
    title.className = 'shop-modal__title';
    title.textContent = heading;
    const lead = document.createElement('p');
    lead.className = 'shop-modal__hint';
    lead.textContent = hint;
    const field = document.createElement('input');
    field.className = 'login__input';
    field.autocomplete = 'off';
    field.spellcheck = false;
    field.placeholder = expected;
    field.setAttribute('aria-label', fieldLabel);
    const error = document.createElement('p');
    error.className = 'login__error';
    error.setAttribute('role', 'alert');
    error.hidden = true;

    const actions = document.createElement('div');
    actions.className = 'shop-modal__actions';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'shop-modal__button';
    cancel.textContent = uiText('Annuler');
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'shop-modal__button shop-modal__button--danger';
    confirm.textContent = uiText('Supprimer');
    confirm.disabled = true;
    actions.append(cancel, confirm);

    const matches = (): boolean => field.value.trim().toLowerCase() === expected.toLowerCase();
    let busy = false;
    const finish = (deleted: boolean): void => {
      window.removeEventListener('keydown', onKeyDown, true);
      root.remove();
      resolve(deleted);
    };
    const submit = (): void => {
      if (busy || !matches()) return;
      busy = true;
      confirm.disabled = true;
      error.hidden = true;
      remove(field.value.trim())
        .then(() => {
          finish(true);
        })
        .catch((cause: unknown) => {
          busy = false;
          confirm.disabled = !matches();
          error.textContent = uiText(
            cause instanceof AuthError ? cause.message : 'Suppression impossible',
          );
          error.hidden = false;
        });
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (!busy) finish(false);
      } else if (event.key === 'Enter' && document.activeElement === field) {
        event.preventDefault();
        submit();
      }
      // The game must not read the typed name as shortcuts.
      if (document.activeElement === field) event.stopPropagation();
    };

    field.addEventListener('input', () => {
      confirm.disabled = busy || !matches();
    });
    cancel.addEventListener('click', () => {
      if (!busy) finish(false);
    });
    confirm.addEventListener('click', submit);
    root.addEventListener('click', (event) => {
      if (event.target === root && !busy) finish(false);
    });

    frame.append(
      title,
      lead,
      field,
      error,
      actions,
      closeButton('Annuler (Échap)', () => {
        if (!busy) finish(false);
      }),
    );
    root.append(frame);
    document.body.append(root);
    window.addEventListener('keydown', onKeyDown, true);
    field.focus();
  });
}
